import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useScroll, useTransform } from 'framer-motion';
import ScrollReveal from '@/components/base/ScrollReveal';

export default function Hero() {
  const { t } = useTranslation();
  const heroRef = useRef<HTMLDivElement>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });

  const contentY = useTransform(scrollYProgress, [0, 0.5], [0, -60]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  const laptopY = useTransform(scrollYProgress, [0, 0.5], [0, -120]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!heroRef.current) return;
      const rect = heroRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      setMousePos({ x, y });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const floatingIcons = [
    { icon: 'ri-facebook-circle-fill', color: 'bg-blue-500/10 text-blue-600', size: 'w-12 h-12', pos: 'top-10 right-[15%]', delay: 0 },
    { icon: 'ri-google-fill', color: 'bg-red-100/60 text-red-500', size: 'w-10 h-10', pos: 'bottom-[25%] right-[5%]', delay: 0.3 },
    { icon: 'ri-robot-2-line', color: 'bg-violet-100/60 text-violet-600', size: 'w-10 h-10', pos: 'top-[35%] left-[5%]', delay: 0.6 },
    { icon: 'ri-node-tree', color: 'bg-emerald-100/60 text-emerald-600', size: 'w-9 h-9', pos: 'bottom-[10%] left-[18%]', delay: 0.9 },
  ];

  const trustItems = [
    { icon: 'ri-check-double-line', key: 'hero.trust1' },
    { icon: 'ri-bar-chart-grouped-line', key: 'hero.trust2' },
    { icon: 'ri-settings-4-line', key: 'hero.trust3' },
    { icon: 'ri-rocket-line', key: 'hero.trust4' },
  ];

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen bg-white flex items-center overflow-hidden"
    >
      {/* Subtle background light effects */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        {/* Main soft glow - top right */}
        <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] rounded-full bg-red-50/50 blur-[150px]"></div>
        {/* Secondary warm glow - bottom left */}
        <div className="absolute bottom-[-10%] left-[-5%] w-[500px] h-[500px] rounded-full bg-amber-50/40 blur-[130px]"></div>
        {/* Subtle center glow */}
        <div className="absolute top-[40%] left-[45%] w-[300px] h-[300px] rounded-full bg-neutral-100/60 blur-[100px]"></div>

        {/* Thin red light streaks behind devices */}
        <div
          className="absolute top-[20%] right-[10%] w-[1px] h-[300px] bg-gradient-to-b from-transparent via-red-400/15 to-transparent origin-top animate-reveal-line"
          style={{ transform: `rotate(-15deg) translateX(${mousePos.x * -15}px) translateY(${mousePos.y * -15}px)` }}
        ></div>
        <div
          className="absolute top-[30%] right-[20%] w-[1px] h-[200px] bg-gradient-to-b from-transparent via-red-400/10 to-transparent origin-top animate-reveal-line"
          style={{ transform: `rotate(8deg) translateX(${mousePos.x * -10}px) translateY(${mousePos.y * -10}px)`, animationDelay: '0.3s' }}
        ></div>

        {/* Ultra subtle dot grid */}
        <div
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: 'radial-gradient(circle, #000 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        ></div>
      </div>

      {/* Very subtle animated particles */}
      <div className="absolute inset-0 z-[1] overflow-hidden pointer-events-none">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full bg-red-300/15"
            style={{
              width: `${Math.random() * 2.5 + 1}px`,
              height: `${Math.random() * 2.5 + 1}px`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -20, 0],
              opacity: [0, 0.5, 0],
              scale: [0, 1, 0],
            }}
            transition={{
              duration: Math.random() * 6 + 5,
              repeat: Infinity,
              delay: Math.random() * 5,
              ease: 'easeInOut',
            }}
          ></motion.div>
        ))}
      </div>

      {/* Content layer */}
      <motion.div
        className="relative z-10 w-full max-w-[1280px] mx-auto px-6 md:px-10 pt-28 md:pt-36 pb-20 md:pb-28"
        style={{ y: contentY, opacity: contentOpacity }}
      >
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
          {/* LEFT: Text content */}
          <div className="flex-1 w-full lg:max-w-[560px]">
            {/* Tag */}
            <ScrollReveal direction="up" delay={0} duration={0.6}>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gray-50 border border-gray-100 mb-8">
                <div className="w-1.5 h-1.5 rounded-full bg-primary-500"></div>
                <span className="text-gray-500 text-xs md:text-sm font-medium tracking-wide">
                  {t('hero.tag')}
                </span>
              </div>
            </ScrollReveal>

            {/* Headline */}
            <ScrollReveal direction="up" delay={0.1} duration={0.7}>
              <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 leading-[1.08] mb-6 tracking-tight">
                {t('hero.mainTitlePart1')}{' '}
                <span className="text-gradient-red">{t('hero.mainTitleHighlight1')}</span>{' '}
                {t('hero.mainTitlePart2')}{' '}
                <span className="text-gradient-red">{t('hero.mainTitleHighlight2')}</span>
                <br />
                {t('hero.mainTitlePart3')}
              </h1>
            </ScrollReveal>

            {/* Subheadline */}
            <ScrollReveal direction="up" delay={0.2} duration={0.7}>
              <p className="text-gray-500 text-base md:text-lg leading-relaxed mb-10 max-w-[500px]">
                {t('hero.subheadline')}
              </p>
            </ScrollReveal>

            {/* CTA Buttons */}
            <ScrollReveal direction="up" delay={0.3} duration={0.6}>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-10">
                <motion.a
                  href="https://wa.link/em25v1"
                  className="px-7 py-3.5 rounded-full bg-primary-500 text-white font-semibold text-sm md:text-base transition-all duration-300 whitespace-nowrap cursor-pointer shadow-[0_4px_14px_rgba(200,30,30,0.3)] inline-flex items-center gap-2"
                  whileHover={{ scale: 1.04, boxShadow: '0 8px 24px rgba(200,30,30,0.4)' }}
                  whileTap={{ scale: 0.97 }}
                >
                  {t('hero.ctaPrimary')}
                  <i className="ri-arrow-right-line"></i>
                </motion.a>
                <motion.a
                  href="#services"
                  className="px-7 py-3.5 rounded-full border border-gray-200 text-gray-700 font-medium text-sm md:text-base hover:border-gray-300 hover:bg-gray-50 transition-all duration-300 whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.97 }}
                >
                  {t('hero.ctaSecondary')}
                  <i className="ri-arrow-down-line"></i>
                </motion.a>
              </div>
            </ScrollReveal>

            {/* Trust indicators */}
            <ScrollReveal direction="up" delay={0.45} duration={0.6}>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                {trustItems.map((item, i) => (
                  <motion.div
                    key={item.key}
                    className="flex items-center gap-2.5"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + i * 0.1, duration: 0.4 }}
                  >
                    <div className="w-5 h-5 flex items-center justify-center rounded-full bg-primary-50 flex-shrink-0">
                      <i className={`${item.icon} text-[10px] text-primary-500`}></i>
                    </div>
                    <span className="text-sm text-gray-600 font-medium">{t(item.key)}</span>
                  </motion.div>
                ))}
              </div>
            </ScrollReveal>
          </div>

          {/* RIGHT: Device showcase */}
          <div className="flex-1 relative w-full lg:h-[580px] flex items-center justify-center">
            {/* Laptop with parallax */}
            <motion.div
              className="relative z-10"
              style={{ y: laptopY }}
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
            >
              <div
                className="relative w-[460px] md:w-[540px] lg:w-[580px]"
                style={{
                  transform: `perspective(1000px) rotateY(${mousePos.x * 4}deg) rotateX(${mousePos.y * -3}deg)`,
                  transition: 'transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)',
                }}
              >
                <img
                  src="https://static.readdy.ai/image/ea473d33d180c87428df5c28ba90939d/053e4067437eb783243321259e26bbc8.png"
                  alt="Marketing Analytics Dashboard on Laptop"
                  className="w-full h-auto object-contain drop-shadow-[0_20px_40px_rgba(0,0,0,0.08)]"
                />
                {/* Dashboard overlay glow */}
                <div className="absolute inset-[8%_14%_30%_14%] rounded-[4px] pointer-events-none bg-gradient-to-br from-transparent via-red-500/[0.02] to-transparent"></div>
              </div>
            </motion.div>

            {/* Floating platform icons around devices */}
            {floatingIcons.map((item, i) => (
              <motion.div
                key={i}
                className={`absolute z-30 ${item.pos} ${item.size} rounded-2xl ${item.color} flex items-center justify-center backdrop-blur-sm border border-white/50 shadow-sm`}
                animate={{
                  y: [0, -8, 0],
                  scale: [1, 1.05, 1],
                }}
                transition={{
                  duration: 4 + i,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: item.delay,
                }}
                style={{
                  transform: `translate(${mousePos.x * (10 + i * 5)}px, ${mousePos.y * (10 + i * 5)}px)`,
                  transition: 'transform 0.8s cubic-bezier(0.22, 1, 0.36, 1)',
                }}
              >
                <i className={`${item.icon} text-lg md:text-xl`}></i>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-10"
        animate={{ opacity: [0.4, 1, 0.4] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
      >
        <span className="text-gray-400 text-[11px] tracking-widest uppercase">{t('hero.scroll')}</span>
        <div className="w-5 h-8 rounded-full border border-gray-200 flex items-start justify-center p-1">
          <motion.div
            className="w-1 h-2 rounded-full bg-primary-500"
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
          ></motion.div>
        </div>
      </motion.div>
    </section>
  );
}